import type {ReactNode} from 'react';
import React, {isValidElement} from 'react';

export const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const INCLUDED_TAGS = ['p', 'span', 'div'];

const highlightTextNode = (text: string, highlight: string): ReactNode[] => {
  const regex = new RegExp(escapeRegExp(highlight), 'gi');
  const parts = text.split(regex);
  const matches = text.match(regex) || [];
  const result: ReactNode[] = [];

  parts.forEach((part, i) => {
    if (part) {
      result.push(part);
    }
    const match = matches[i];
    if (match) {
      result.push(
        // eslint-disable-next-line react/no-array-index-key
        <span className='background-highlight' key={`hl-${i}`}>
          {match}
        </span>
      );
    }
  });

  return result;
};

export const mapReactTreeWithHighlights = (node: ReactNode, highlight?: string): ReactNode => {
  if (!highlight || !highlight.trim()) {
    return node;
  }

  if (typeof node === 'string') {
    return highlightTextNode(node, highlight);
  }

  if (!isValidElement<{children?: React.ReactNode}>(node)) {
    return node;
  }

  const {type} = node;
  const tagName = typeof type === 'string' ? type : undefined;

  // skip highlighting inside excluded containers
  if (tagName && !INCLUDED_TAGS.includes(tagName.toLowerCase())) {
    return node;
  }

  const {children, ...restProps} = node.props || {};

  const mappedChildren = React.Children.map(children, (child) =>
    mapReactTreeWithHighlights(child, highlight)
  );

  return React.cloneElement(node, restProps, mappedChildren);
};
